import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Download, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/adminAuth";

/* 🎬 PROMO STUDIO (admin) — AI ad videos via fal.ai + ready-made poster pack.
   Honest-marketing rule: every asset hypes fun/community, never profits. */
const THEMES = [
  ["overview", "🚀 Platform Overview"],
  ["penalty", "⚽ Penalty Party"],
  ["heli", "🚁 Heli Ride"],
  ["bots", "🤖 Trading Bots"],
  ["community", "🌍 Global Community"],
];
const LANGS = [["en", "🇺🇸 English"], ["fr", "🇫🇷 Français"], ["es", "🇪🇸 Español"], ["pt", "🇧🇷 Português"]];
const AD_VIDEOS = [
  ["/promo/vts_ad_full_en.mp4", "🎬 FULL 32s AD · 🇺🇸 English"],
  ["/promo/vts_ad_full_pt.mp4", "🎬 FULL 32s AD · 🇧🇷 Português"],
  ["/promo/vts_ad_full_es.mp4", "🎬 FULL 32s AD · 🇪🇸 Español"],
  ["/promo/vts_ad_full_fr.mp4", "🎬 FULL 32s AD · 🇫🇷 Français"],
  ["/promo/overview_en.mp4", "🚀 Platform Overview · EN"],
  ["/promo/penalty_en.mp4", "⚽ Penalty Arcade · EN"],
];
const TG_VIDEOS = [
  ["/promo/penalty_en_tg.mp4", "⚽🚁🤖 AD Teaser EN · 16:9 · 3.8MB"],
  ["/promo/teaser_pt_tg.mp4", "⚽ AD Teaser PT · 16:9 · 3.5MB"],
  ["/promo/teaser_es_tg.mp4", "⚽ AD Teaser ES · 16:9 · 3.8MB"],
  ["/promo/teaser_fr_tg.mp4", "⚽ AD Teaser FR · 16:9 · 3.8MB"],
  ["/promo/vts_ad_full_en_tg.mp4", "🎬 Full EN · 9:16 for posts · 4.1MB"],
  ["/promo/vts_ad_full_pt_tg.mp4", "🎬 Full PT · 9:16 for posts · 4.0MB"],
  ["/promo/vts_ad_full_es_tg.mp4", "🎬 Full ES · 9:16 for posts · 3.7MB"],
  ["/promo/vts_ad_full_fr_tg.mp4", "🎬 Full FR · 9:16 for posts · 3.8MB"],
];
const POSTERS = [
  ["/promo/penalty_en.jpg", "⚽ Penalty · English"],
  ["/promo/heli_fr.jpg", "🚁 Heli · Français"],
  ["/promo/party_es.jpg", "🌍 Party · Español"],
  ["/promo/bots_pt.jpg", "🤖 Bots · Português"],
];

const PromoStudio = () => {
  const [theme, setTheme] = useState("penalty");
  const [lang, setLang] = useState("en");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [videos, setVideos] = useState([]);

  const load = () => api.get("/admin/promo/videos", { headers: authHeader() })
    .then((r) => setVideos(r.data.videos || [])).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/promo/video", { theme, language: lang, note }, { headers: authHeader() });
      toast.success("Video queued — ready in ~1-3 minutes (list refreshes itself)");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start generation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="promo-studio" className="rounded-xl border border-vts-line bg-vts-panel p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Clapperboard className="w-4 h-4 text-vts-blue" />
        <h3 className="text-sm font-bold text-white font-mono">🎬 PROMO STUDIO</h3>
        <span className="text-[9px] font-mono text-vts-silver/50">AI ads for TikTok/Reels/WhatsApp · hype the fun, never promise profits</span>
      </div>

      {/* video generator */}
      <div className="flex items-end gap-2 flex-wrap">
        <label className="block">
          <span className="text-[9px] font-mono uppercase text-vts-silver/50">Theme</span>
          <select data-testid="promo-theme" value={theme} onChange={(e) => setTheme(e.target.value)}
            className="block h-9 rounded-md border border-vts-line bg-vts-surface px-2 text-xs font-mono text-white">
            {THEMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[9px] font-mono uppercase text-vts-silver/50">Language</span>
          <select data-testid="promo-lang" value={lang} onChange={(e) => setLang(e.target.value)}
            className="block h-9 rounded-md border border-vts-line bg-vts-surface px-2 text-xs font-mono text-white">
            {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="block flex-1 min-w-[160px]">
          <span className="text-[9px] font-mono uppercase text-vts-silver/50">Extra direction (optional)</span>
          <input data-testid="promo-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. show the Accra skyline"
            className="block w-full h-9 rounded-md border border-vts-line bg-vts-surface px-2 text-xs font-mono text-white placeholder:text-vts-silver/40" />
        </label>
        <button data-testid="promo-generate" disabled={busy} onClick={generate}
          className="h-9 px-4 rounded-md bg-vts-blue text-white text-[11px] font-bold hover:bg-vts-blue/80 transition disabled:opacity-40">
          {busy ? "Starting…" : "Generate video"}
        </button>
      </div>

      {/* generated videos */}
      <div data-testid="promo-video-list" className="space-y-1.5">
        {videos.length === 0 && <p className="text-[10px] font-mono text-vts-silver/40">No videos yet — generate your first ad above.</p>}
        {videos.map((v) => (
          <div key={v.id} className="flex items-center gap-2 text-[11px] font-mono flex-wrap">
            <span className="text-white/80">{THEMES.find(([k]) => k === v.theme)?.[1] || v.theme}</span>
            <span className="text-vts-silver/50 uppercase">{v.language}</span>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${v.status === "done" ? "bg-emerald-500/15 text-emerald-300" : v.status === "failed" ? "bg-vts-loss/15 text-vts-loss" : "bg-amber-400/15 text-amber-300 animate-pulse"}`}>
              {v.status}
            </span>
            {v.status === "done" && v.video_url && (
              <a data-testid={`promo-download-${v.id}`} href={v.video_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-vts-blue hover:underline">
                <Download className="w-3 h-3" /> {v.video_url.startsWith("/promo/") ? "download (saved in app)" : "download now (link expires)"}
              </a>
            )}
            {v.status === "failed" && <span className="text-vts-loss/80">{v.error}</span>}
          </div>
        ))}
        <button onClick={load} className="inline-flex items-center gap-1 text-[10px] font-mono text-vts-silver/50 hover:text-white transition">
          <RefreshCw className="w-3 h-3" /> refresh
        </button>
      </div>

      {/* ad video pack */}
      <div className="space-y-1.5">
        <p className="text-[10px] font-mono text-vts-silver/60 font-bold">🎥 AD VIDEO PACK — 9:16 with sound, ready for TikTok/Reels/WhatsApp status ads</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {AD_VIDEOS.map(([src, label]) => (
            <div key={src} className="space-y-1">
              <video data-testid={`promo-advideo-${label.replace(/\W+/g, "-").toLowerCase()}`} src={src} controls preload="metadata" playsInline
                className="rounded-lg border border-vts-line w-full aspect-[9/16] object-cover bg-black" />
              <a href={src} download className="inline-flex items-center gap-1 text-[9px] font-mono text-vts-blue hover:underline">
                <Download className="w-3 h-3" /> {label}
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* telegram-ready downloads (<5MB ad limit) */}
      <div className="space-y-1">
        <p className="text-[10px] font-mono text-vts-silver/60 font-bold">✈️ TELEGRAM-READY (under 5MB ad limit) — download for ads.telegram.org</p>
        <div className="flex flex-wrap gap-2">
          {TG_VIDEOS.map(([src, label]) => (
            <a key={src} href={src} download data-testid={`promo-tg-${label.replace(/\W+/g, "-").toLowerCase()}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-vts-line bg-vts-surface text-[10px] font-mono text-vts-blue hover:border-vts-blue/50 transition">
              <Download className="w-3 h-3" /> {label}
            </a>
          ))}
        </div>
      </div>

      {/* poster pack */}
      <div className="space-y-1.5">
        <p className="text-[10px] font-mono text-vts-silver/60 font-bold">🖼 READY-MADE POSTER PACK — long-press / right-click to save, post on WhatsApp status, Telegram & IG stories</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {POSTERS.map(([src, label]) => (
            <a key={src} href={src} download target="_blank" rel="noreferrer" className="group block">
              <img src={src} alt={label} loading="lazy" className="rounded-lg border border-vts-line group-hover:border-vts-blue transition w-full" />
              <span className="block mt-1 text-[9px] font-mono text-vts-silver/60">{label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PromoStudio;
